import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { CURRENT_MEMBERSHIP_ID_QUERY_PARAM } from 'src/constants/constants'
import { useAuth } from 'src/contexts/authContext'
import { useSessionStorageContext } from 'src/contexts/SessionStorageContext'
import { Membership, User } from 'src/types'
import authClient from 'src/utils/auth'

interface UseSyncCurrentMembershipResult {
  currentMembershipFromUrl?: Membership
  effectiveMembership?: Membership
}

export const useSyncCurrentMembership = (user?: User): UseSyncCurrentMembershipResult => {
  const location = useLocation()
  const { currentMembershipId, setCurrentMembershipId } = useSessionStorageContext()
  const { updateUser } = useAuth()

  const params = new URLSearchParams(location.search)
  const currentMembershipIdFromUrl = parseInt(params.get(CURRENT_MEMBERSHIP_ID_QUERY_PARAM) ?? '')
  const currentMembershipFromUrl = user?.memberships.find(
    membership => membership.id === currentMembershipIdFromUrl
  )

  const currentMembership = user?.memberships.find(
    membership => membership.id === currentMembershipId
  )

  useEffect(() => {
    const isCurrentMembershipNotSet = !currentMembership
    const isCurrentMembershipNotFoundAmongUserMemberships =
      !!currentMembershipId && isCurrentMembershipNotSet

    if (currentMembershipFromUrl) {
      if (currentMembershipFromUrl.id !== currentMembershipId) {
        // The user object (and the counts it carries, e.g. the mapping badge) was
        // fetched using the previous Current-Membership header. Refetch it now that
        // the correct membership id has been persisted, otherwise it stays stale.
        setCurrentMembershipId(currentMembershipFromUrl.id)
        authClient.getUser().then(updateUser)
      }
    } else if (user && (!currentMembershipId || isCurrentMembershipNotFoundAmongUserMemberships)) {
      const defaultMembershipId = user.memberships.find(membership => membership.isDefault)?.id

      if (defaultMembershipId) {
        setCurrentMembershipId(defaultMembershipId)
      }
    }
  }, [JSON.stringify(user), currentMembershipIdFromUrl])

  return {
    currentMembershipFromUrl,
    effectiveMembership: currentMembershipFromUrl || currentMembership,
  }
}
